// BUG_REPORTER — TEMPORARY FEATURE. Delete this folder per docs/BUG_REPORTER.md.
//
// The left-hand list: one compact row per report — ticket and status, the title,
// then where, who and when — and nothing that expands in place. What a table row
// used to open into now lives in the detail panel beside it (owner's decision,
// 2026-09-14, docs/plans/DECISIONS.md D37).
//
// D47 (2026-09-18): every report keeps its own row, so a search or a ticket number
// still finds it. A report linked to an earlier one says which; an original that
// others are linked to says how many times it was seen.

import React from 'react';
import type { BugReportSummary } from '../../lib/bugReportApi';
import { formatDate } from '../../utils/dateFormat';
import { FOCUS } from '../../components/ui/tokens';
import { BugStatusBadge } from './BugStatusBadge';

const CHIP = 'inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600';

function linkChip(report: BugReportSummary): string | null {
  if (report.linkedTo) {
    return report.linkedTo.kind === 'came_back'
      ? `Came back after ${report.linkedTo.ticketNumber}`
      : `Same as ${report.linkedTo.ticketNumber}`;
  }
  const seen = (report.instances ?? []).length;
  return seen > 0 ? `Seen ${seen + 1} times` : null;
}

export const BugReportList: React.FC<{
  reports: BugReportSummary[];
  activeId: string | null;
  onSelect: (id: string) => void;
}> = ({ reports, activeId, onSelect }) => (
  <ul aria-label="Reports" className="divide-y divide-slate-100">
    {reports.map((report) => {
      const active = report.id === activeId;
      const meta = [
        report.pageLabel || null,
        report.isMine ? 'You' : report.reporterName || null,
        formatDate(report.createdAt) || null,
      ].filter(Boolean);
      const chip = linkChip(report);
      return (
        <li key={report.id}>
          <button
            type="button"
            onClick={() => onSelect(report.id)}
            aria-current={active ? 'true' : undefined}
            className={[
              'block w-full border-l-2 px-4 py-3 text-left transition-colors',
              active ? 'border-primary-500 bg-primary-50' : 'border-transparent hover:bg-slate-50',
              FOCUS,
            ].join(' ')}
          >
            <span className="flex items-center justify-between gap-2">
              <span className="font-mono text-xs text-slate-500">{report.ticketNumber}</span>
              <BugStatusBadge status={report.status} />
            </span>
            <span className="mt-1.5 line-clamp-2 block break-words text-sm font-medium leading-snug text-slate-900">
              {report.title}
            </span>
            {meta.length > 0 ? (
              <span className="mt-1 block truncate text-xs text-slate-500">{meta.join(' · ')}</span>
            ) : null}
            {chip ? <span className={`mt-1.5 ${CHIP}`}>{chip}</span> : null}
          </button>
        </li>
      );
    })}
  </ul>
);
